import { useState, useRef, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight, Clock, X, ZoomIn, ImageOff } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export function ProjectDevelopmentUpdates({ projectId }: { projectId?: string } = {}) {
  const params = useParams<{ id: string }>();
  const id = projectId || params.id;

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [imgError, setImgError] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: updates, isLoading } = useQuery({
    queryKey: ['project-updates', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_updates' as any)
        .select('*')
        .eq('project_id', id!)
        .order('update_date', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!id,
  });

  const photoUpdates = useMemo(() => (updates || []).filter((u: any) => !!u.image_url), [updates]);

  // Keyboard navigation in lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setLightboxIdx(i => (i === null ? 0 : (i + 1) % photoUpdates.length));
      if (e.key === 'ArrowLeft') setLightboxIdx(i => (i === null ? 0 : (i - 1 + photoUpdates.length) % photoUpdates.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, photoUpdates.length]);

  if (isLoading) return <Skeleton className="h-40 rounded-xl" />;
  if (!updates || updates.length === 0) return null;

  const selected = updates[selectedIdx];
  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const formatShort = (d: string) => {
    const dt = new Date(d);
    return { month: dt.toLocaleDateString('en-US', { month: 'short' }), year: dt.getFullYear().toString() };
  };

  const openLightbox = () => {
    if (!selected?.image_url) return;
    const idx = photoUpdates.findIndex((u: any) => u.id === selected.id);
    setImgError(false);
    setLightboxIdx(idx >= 0 ? idx : 0);
  };

  const lightboxItem = lightboxIdx !== null ? photoUpdates[lightboxIdx] : null;

  return (
    <section>
      <h2 className="font-display text-lg md:text-xl font-bold mb-3 md:mb-4">Development Updates</h2>
      <div className="border rounded-xl p-3 md:p-6 max-w-full overflow-hidden">
        <div className="flex items-center gap-2 mb-4 md:mb-6 max-w-full">
          <button onClick={() => scrollRef.current?.scrollBy({ left: -120, behavior: 'smooth' })} className="shrink-0 p-1 rounded-full hover:bg-muted transition-colors hidden md:block">
            <ChevronLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          <div ref={scrollRef} className="flex gap-2 overflow-x-auto no-scrollbar flex-1 min-w-0">
            {updates.map((u: any, i: number) => {
              const { month, year } = formatShort(u.update_date);
              return (
                <button key={u.id} onClick={() => setSelectedIdx(i)}
                  className={`shrink-0 px-3 md:px-4 py-2 rounded-full text-xs font-medium transition-all text-center leading-tight ${i === selectedIdx ? 'bg-primary text-primary-foreground ring-2 ring-primary/30' : 'text-muted-foreground hover:bg-muted'}`}>
                  {month}<br />{year}
                </button>
              );
            })}
          </div>
          <button onClick={() => scrollRef.current?.scrollBy({ left: 120, behavior: 'smooth' })} className="shrink-0 p-1 rounded-full hover:bg-muted transition-colors hidden md:block">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="border-t pt-4">
          <span className="inline-block px-3 py-1 bg-muted rounded-full text-xs font-medium mb-3 md:mb-4">{formatDate(selected.update_date)}</span>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {selected.image_url && (
              <button
                type="button"
                onClick={openLightbox}
                title="Click to enlarge"
                aria-label="Open construction update photo"
                className="relative w-full sm:w-44 aspect-video sm:aspect-auto sm:h-28 min-h-[44px] rounded-lg bg-muted shrink-0 overflow-hidden cursor-zoom-in group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <img
                  src={selected.image_url}
                  alt={selected.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="flex items-center gap-1.5 text-white text-xs font-medium px-2.5 py-1 rounded-full bg-black/60 backdrop-blur">
                    <ZoomIn className="h-3.5 w-3.5" /> View photo
                  </span>
                </div>
              </button>
            )}
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-sm break-words">{selected.title}</h4>
              {selected.description && <p className="text-xs text-muted-foreground mt-1 break-words">{selected.description}</p>}
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                <Clock className="h-3 w-3 shrink-0" /> <span className="truncate">Posted {formatDate(selected.update_date)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={lightboxIdx !== null} onOpenChange={(o) => !o && setLightboxIdx(null)}>
        <DialogContent className="max-w-5xl p-0 bg-black border-0 overflow-hidden">
          {/* Counter */}
          {photoUpdates.length > 1 && lightboxIdx !== null && (
            <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
              {lightboxIdx + 1} / {photoUpdates.length}
            </div>
          )}
          {/* Close */}
          <button
            onClick={() => setLightboxIdx(null)}
            className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          {/* Prev / Next */}
          {photoUpdates.length > 1 && (
            <>
              <button
                onClick={() => { setImgError(false); setLightboxIdx(i => (i === null ? 0 : (i - 1 + photoUpdates.length) % photoUpdates.length)); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                aria-label="Previous photo"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => { setImgError(false); setLightboxIdx(i => (i === null ? 0 : (i + 1) % photoUpdates.length)); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                aria-label="Next photo"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          {lightboxItem && (
            <div className="flex items-center justify-center w-full min-h-[50vh] max-h-[90vh] bg-black">
              {imgError ? (
                <div className="flex flex-col items-center gap-2 text-white/70 py-16">
                  <ImageOff className="h-10 w-10" />
                  <span className="text-sm">Image could not be loaded</span>
                </div>
              ) : (
                <img
                  src={lightboxItem.image_url}
                  alt={lightboxItem.title}
                  decoding="async"
                  onError={() => setImgError(true)}
                  className="w-auto h-auto max-w-full max-h-[90vh] object-contain"
                />
              )}
            </div>
          )}

          {lightboxItem && (
            <div className="p-4 bg-background text-foreground">
              <h4 className="font-semibold text-sm">{lightboxItem.title}</h4>
              {lightboxItem.description && <p className="text-xs text-muted-foreground mt-1">{lightboxItem.description}</p>}
              <p className="text-[11px] text-muted-foreground mt-1">{formatDate(lightboxItem.update_date)}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
